import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { teamsKey, type TeamRole } from './useTeam'
import { toAppError, type AppError } from '../lib/errors'
import type { Row } from '../lib/db-types'

/**
 * Team collaboration hooks — members, invitations, and the accept/leave
 * mutations. Split from `useTeam.ts` which holds the top-level "which teams
 * am I in" state; these handle the inside-a-team surfaces (Settings → Team).
 *
 * Invitation model: a team owner creates a row in `team_invitations` with
 * a random token + expiry. The owner copies the resulting invite URL
 * (`?invite=<token>`) and shares out-of-band (email, Slack, etc). The
 * recipient opens the URL in an authed session and the `?invite` handler
 * calls `accept_team_invitation(token)` — a SECURITY DEFINER RPC that
 * validates the token and materialises the membership.
 */

/**
 * Flattened result of `get_team_members` — team_members row joined with
 * display_name/email from profiles.
 *
 * Intentionally hand-typed, NOT derived from `RpcReturn<'get_team_members'>`:
 *   1. The RPC types declare display_name/email as non-null but at runtime
 *      they can be null (LEFT JOIN returns null for missing profiles).
 *   2. Mapped types over RPC returns confuse TanStack Query's generic
 *      inference (end up with `never[] | ...` on consumers).
 */
export interface TeamMemberRow {
  user_id: string
  team_id: string
  // owner > admin > member. See 2026-05-15-role-hierarchy.sql.
  role: TeamRole
  joined_at: string
  invited_by: string | null
  display_name: string | null
  email: string
}

/**
 * Shape of a `team_invitations` row. `role` is `text` in the DB but only
 * `'member'` is ever inserted today; narrow for exhaustiveness if we ever
 * add per-row roles.
 */
export type TeamInvitationRow = Omit<Row<'team_invitations'>, 'role'> & {
  role: 'member'
}

export const teamMembersKey = (teamId: string | undefined): QueryKey =>
  ['teamMembers', teamId ?? 'none']
export const teamInvitationsKey = (teamId: string | undefined): QueryKey =>
  ['teamInvitations', teamId ?? 'none']

// ──────────────────────────────────────────────────────────────────────────
// Members of a team
// ──────────────────────────────────────────────────────────────────────────

export function useTeamMembers(teamId: string | null | undefined) {
  const key = teamMembersKey(teamId ?? undefined)
  const query = useQuery<TeamMemberRow[]>({
    queryKey: key,
    enabled: !!teamId,
    queryFn: async () => {
      // We can't use a PostgREST embed (`profile:profiles(...)`) because
      // there's no FK from `team_members.user_id` to `profiles.id` — both
      // reference `auth.users(id)` independently. Even with two queries,
      // profiles RLS blocks cross-user reads. The `get_team_members` RPC
      // (SECURITY DEFINER) handles both gaps in one round-trip.
      const { data, error } = await supabase.rpc('get_team_members', { p_team_id: teamId! })
      if (error) throw error
      return ((data ?? []) as Array<{
        user_id: string
        team_id: string
        role: TeamRole
        joined_at: string
        invited_by: string | null
        display_name: string | null
        email: string | null
      }>).map((row) => ({
        user_id: row.user_id,
        team_id: row.team_id,
        role: row.role,
        joined_at: row.joined_at,
        invited_by: row.invited_by,
        display_name: row.display_name ?? null,
        email: row.email ?? '',
      }))
    },
  })
  return {
    members: query.data ?? [],
    loading: !!teamId && query.isPending,
    error: query.error ? toAppError(query.error) : null as AppError | null,
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Pending invitations — for the owner to see / manage
// ──────────────────────────────────────────────────────────────────────────

export function useTeamInvitations(teamId: string | null | undefined) {
  const key = teamInvitationsKey(teamId ?? undefined)
  const query = useQuery<TeamInvitationRow[]>({
    queryKey: key,
    enabled: !!teamId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('team_id', teamId!)
        .is('accepted_at', null)
        .is('revoked_at', null)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as TeamInvitationRow[]
    },
  })
  return {
    invitations: query.data ?? [],
    loading: !!teamId && query.isPending,
    error: query.error ? toAppError(query.error) : null as AppError | null,
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Create invitation
// ──────────────────────────────────────────────────────────────────────────

/**
 * Invite result envelope. The invitation row is always returned when the
 * insert succeeded — email is best-effort on top of that, so the UI can
 * still surface the copy-link fallback if Resend / the edge function is
 * down.
 */
export interface InviteResult {
  invitation: TeamInvitationRow
  emailSent: boolean
  emailError: string | null
}

/**
 * Creates an invitation row via the `invite_team_member` RPC
 * (SECURITY DEFINER), then fires the `send-team-invite` edge function
 * which emails the invitee via Resend. The RPC enforces:
 *   - caller is a team owner (`not_team_owner` otherwise)
 *   - email shape is valid (`invalid_email`)
 *   - invitee isn't already a member (`already_member`)
 *   - no duplicate pending invite for (team_id, lower(email)) — the
 *     partial unique index is belt to the RPC's dedupe check
 *   - generates a 128-bit token server-side (was client-generated pre-migration)
 *   - audit row in-transaction
 *
 * Email failure is non-fatal — the row is already persisted and the UI
 * still copies the link to the clipboard. This keeps invite creation
 * resilient to transient email-provider outages.
 */
export function useInviteTeamMember(teamId: string | null | undefined) {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation<InviteResult, Error, string>({
    mutationKey: ['teamInvitations', 'create', teamId],
    mutationFn: async (email: string) => {
      if (!user) throw new Error('Not authenticated')
      if (!teamId) throw new Error('No team')
      const { data, error } = await supabase.rpc('invite_team_member', {
        p_team_id: teamId,
        p_email: email,
      })
      if (error) throw error
      const invitation = data as unknown as TeamInvitationRow

      // Best-effort email. We intentionally swallow the error and surface
      // it to the UI layer via the envelope — the invitation itself is
      // already good.
      let emailSent = false
      let emailError: string | null = null
      try {
        const { error: fnError } = await supabase.functions.invoke('send-team-invite', {
          body: { invitationId: invitation.id },
        })
        if (fnError) {
          // supabase-js swallows the response body by default. Unwrap it so
          // the toast tells us the actual failure ('resend_not_configured',
          // 'email_send_failed', etc.) instead of a generic "non-2xx".
          emailError = await extractFunctionError(fnError)
        } else {
          emailSent = true
        }
      } catch (err) {
        emailError = (err as Error).message || 'Email failed to send'
      }

      return { invitation, emailSent, emailError }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamInvitationsKey(teamId ?? undefined) })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Bulk invite — fan out N single-invite calls with bounded concurrency
// ──────────────────────────────────────────────────────────────────────────

/**
 * Per-email status emitted while a bulk invite is in flight. `pending` is
 * the initial state (queued); `sending` is the narrow "RPC + edge function
 * in flight" window; terminal states are `emailed` / `sent` / `error`.
 *
 *   emailed — RPC succeeded AND Resend delivered
 *   sent    — RPC succeeded, email layer failed (copy-link fallback path)
 *   error   — RPC itself rejected (not_team_owner, already_member, etc.)
 */
export type BulkInviteStatus = 'pending' | 'sending' | 'emailed' | 'sent' | 'error'

export interface BulkInviteRow {
  email: string
  status: BulkInviteStatus
  /** Invitation token on success — enables the copy-link fallback. */
  token: string | null
  /** RPC error code (e.g. `already_member`, `duplicate_pending`). */
  errorCode: string | null
  /** Edge-function / Resend error detail, only set on status='sent'. */
  emailError: string | null
}

/** Concurrency cap for fanout. Keeps the browser from opening a flood of
 * sockets if a pathological paste lands (whole address book). Users see no
 * practical difference at N<=10 — sends at higher N just queue behind. */
const BULK_INVITE_CONCURRENCY = 10

/**
 * Bulk-invite hook. The hook does NOT hold row state — the caller owns a
 * list of `BulkInviteRow` and passes an `onRowUpdate` callback that's
 * fired at every state transition (pending → sending → terminal). That
 * keeps the UI fully responsive and lets the caller persist rows across
 * multiple submits if they want (e.g. an append-only results log).
 *
 * Post-2026-05-16: the server-side `invite_team_member` RPC rejects
 * off-domain invitees with `invite_domain_not_registered`. To keep the
 * UX tidy we pre-filter the batch against the team's registered domains
 * and return the off-domain rows separately via the returned envelope
 * so the UI can surface a single "N emails not on a registered domain"
 * warning instead of N individual per-row errors.
 *
 * Validation is otherwise server-side — we don't short-circuit on
 * already-member / duplicate-pending because:
 *   1. The client's view of members/invitations may be stale.
 *   2. The RPC's error codes are already per-email and actionable.
 *   3. Duplicating the guards would be a second source of truth.
 *
 * Reuses the same `invite_team_member` RPC + `send-team-invite` edge
 * function as `useInviteTeamMember`; the logic is copied rather than
 * routed through the single-invite mutation so per-call cache
 * invalidation doesn't fire N times. We do ONE invalidation at the end.
 */
export interface BulkInviteOutcome {
  /** Emails the caller passed that ended up being sent (or attempted). */
  attempted: string[]
  /** Emails filtered out locally because they aren't on a registered domain. */
  offDomain: string[]
}

export function useBulkInviteTeamMembers(teamId: string | null | undefined) {
  const { user } = useAuth()
  const qc = useQueryClient()

  const invite = useCallback(
    async (
      emails: string[],
      onRowUpdate: (email: string, patch: Partial<BulkInviteRow>) => void,
      /**
       * Lowercase list of domains registered on this team. The UI sources
       * this from `useTeamDomains(teamId).domains`. An empty/undefined
       * list disables the pre-filter (equivalent to "send everything and
       * let the server reject"). Passing `[]` explicitly means "filter
       * everything out" — defensive against a loading-state race where
       * the caller has `domains` pending.
       */
      registeredDomains?: string[],
    ): Promise<BulkInviteOutcome> => {
      if (!user) throw new Error('Not authenticated')
      if (!teamId) throw new Error('No team')
      if (emails.length === 0) return { attempted: [], offDomain: [] }

      // Pre-filter: split into on-domain vs off-domain buckets. When the
      // caller didn't pass a domain list (undefined), we don't filter —
      // preserves backward compat for any consumer that hasn't been
      // updated yet.
      let attempted = emails
      const offDomain: string[] = []
      if (registeredDomains !== undefined) {
        const normalized = new Set(registeredDomains.map((d) => d.toLowerCase()))
        attempted = []
        for (const email of emails) {
          const at = email.lastIndexOf('@')
          const domain = at >= 0 ? email.slice(at + 1).toLowerCase() : ''
          if (domain && normalized.has(domain)) {
            attempted.push(email)
          } else {
            offDomain.push(email)
          }
        }
      }

      if (attempted.length === 0) {
        // Nothing to send. Still return the envelope so the UI can surface
        // the off-domain warning.
        return { attempted, offDomain }
      }

      // Simple worker-pool concurrency limiter. Each worker pulls off the
      // shared queue until it's empty. No library dependency, no priority
      // queue needed — order of completion doesn't matter for the UI since
      // rows update in place by email.
      const queue = [...attempted]
      const runOne = async (email: string): Promise<void> => {
        onRowUpdate(email, { status: 'sending' })

        const { data, error } = await supabase.rpc('invite_team_member', {
          p_team_id: teamId,
          p_email: email,
        })
        if (error) {
          onRowUpdate(email, {
            status: 'error',
            errorCode: error.message || 'unknown_error',
          })
          return
        }
        const invitation = data as unknown as TeamInvitationRow

        // Best-effort email. The invitation row is already good either way.
        let emailError: string | null = null
        let emailed = false
        try {
          const { error: fnError } = await supabase.functions.invoke('send-team-invite', {
            body: { invitationId: invitation.id },
          })
          if (fnError) {
            emailError = await extractFunctionError(fnError)
          } else {
            emailed = true
          }
        } catch (err) {
          emailError = (err as Error).message || 'Email failed to send'
        }

        onRowUpdate(email, {
          status: emailed ? 'emailed' : 'sent',
          token: invitation.token,
          emailError,
        })
      }

      const worker = async (): Promise<void> => {
        while (queue.length > 0) {
          const email = queue.shift()
          if (!email) break
          await runOne(email)
        }
      }

      const workerCount = Math.min(BULK_INVITE_CONCURRENCY, attempted.length)
      await Promise.all(Array.from({ length: workerCount }, () => worker()))

      // Single invalidation at the end — cheaper than the N that per-call
      // React Query mutations would trigger, and the pending-invitations
      // list will reflect every successful row on the next refetch.
      qc.invalidateQueries({ queryKey: teamInvitationsKey(teamId) })

      return { attempted, offDomain }
    },
    [teamId, user, qc],
  )

  return { invite }
}

// ──────────────────────────────────────────────────────────────────────────
// Revoke invitation
// ──────────────────────────────────────────────────────────────────────────

/**
 * Revokes a pending invitation via the `revoke_team_invitation` RPC
 * (SECURITY DEFINER). Owner-only authz enforced server-side. Idempotent —
 * revoking an already-revoked invite is a no-op that still returns OK.
 */
export function useRevokeInvitation(teamId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['teamInvitations', 'revoke', teamId],
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase.rpc('revoke_team_invitation', {
        p_invitation_id: invitationId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamInvitationsKey(teamId ?? undefined) })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Remove member
// ──────────────────────────────────────────────────────────────────────────

/**
 * Removes a member via the `remove_team_member` RPC (SECURITY DEFINER).
 * Enforced server-side:
 *   - caller is a team owner (`not_team_owner`)
 *   - caller cannot remove themselves (`cannot_remove_self_use_leave` —
 *     use `useLeaveTeam` instead)
 *   - last owner cannot be removed (`cannot_remove_last_owner`)
 *   - target's `profiles.current_team_id` reset to their personal team
 *     so their UI doesn't land on a team they no longer belong to
 *   - audit row in-transaction
 */
export function useRemoveTeamMember(teamId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['teamMembers', 'remove', teamId],
    mutationFn: async (userId: string) => {
      if (!teamId) throw new Error('No team')
      const { error } = await supabase.rpc('remove_team_member', {
        p_team_id: teamId,
        p_user_id: userId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersKey(teamId ?? undefined) })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Leave team (delete my own membership)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Leaves a team via the `leave_team` RPC (SECURITY DEFINER).
 * Enforced server-side:
 *   - cannot leave a personal team (`cannot_leave_personal_team`)
 *   - if you're the last owner of a multi-member team, RPC raises
 *     `last_owner_must_transfer` — UI surfaces the Transfer-ownership
 *     flow or Delete-team confirmation
 *   - if you're the sole member, the team is deleted (cascades through
 *     team_members / team_invitations / templates via FK set up in the
 *     2026-05-10 migration)
 *   - `profiles.current_team_id` reset to personal team
 *   - audit row in-transaction
 */
export function useLeaveTeam() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['teamMembers', 'leave'],
    mutationFn: async (teamId: string) => {
      if (!user) throw new Error('Not authenticated')
      const { error } = await supabase.rpc('leave_team', { p_team_id: teamId })
      if (error) throw error
      return teamId
    },
    onSuccess: (teamId) => {
      qc.invalidateQueries({ queryKey: teamsKey(user?.id) })
      qc.invalidateQueries({ queryKey: teamMembersKey(teamId) })
      // Team-scoped templates may now be invisible — refetch.
      qc.invalidateQueries({ queryKey: ['templates'] })
      // current_team_id changed server-side; refresh profile.
      qc.invalidateQueries({ queryKey: ['profile', user?.id] })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Transfer ownership — promote another member to owner, demote self to member
// ──────────────────────────────────────────────────────────────────────────

/**
 * Transfers ownership to another member via `transfer_team_ownership`
 * (SECURITY DEFINER). Atomic role swap in a single transaction.
 * Enforced server-side:
 *   - caller is a current owner (`not_team_owner`)
 *   - target is a current member of the same team (`target_not_a_member`)
 *   - target is not the caller (`cannot_transfer_to_self`)
 *   - audit row in-transaction
 *
 * Post-transfer, the caller becomes a `member` and the target becomes
 * the `owner`. If the caller then wants to leave the team, they use
 * `useLeaveTeam` — which is now legal since they're no longer the
 * last owner.
 */
export function useTransferOwnership(teamId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['teamMembers', 'transferOwnership', teamId],
    mutationFn: async (newOwnerUserId: string) => {
      if (!teamId) throw new Error('No team')
      const { error } = await supabase.rpc('transfer_team_ownership', {
        p_team_id: teamId,
        p_new_owner_user_id: newOwnerUserId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersKey(teamId ?? undefined) })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Promote / demote a member (owner-only)
// ──────────────────────────────────────────────────────────────────────────

export type AssignableRole = 'admin' | 'member'

export interface SetTeamMemberRoleArgs {
  userId: string
  /** Only 'admin' or 'member' — use `useTransferOwnership` to promote to owner. */
  role: AssignableRole
}

/**
 * Toggle a team member between `admin` and `member` via
 * `set_team_member_role` (SECURITY DEFINER, added 2026-05-15). Owner-only.
 *
 * Server-side guarantees (see migration for full list):
 *   - caller is the owner                       (`not_team_owner`)
 *   - `role` is one of 'admin' / 'member'       (`invalid_role`)
 *   - target is not self                        (`cannot_change_own_role`)
 *   - target is not the owner                   (`cannot_change_owner_role`)
 *   - target is a current member of the team    (`not_a_member`)
 *
 * The RPC is idempotent when target is already at the requested role — no
 * state change, no audit row.
 *
 * Types note: `set_team_member_role` is a new RPC and not yet in
 * `database.types.ts` (regenerated post-merge). Same `unknown`-cast pattern
 * used by `useTeamDomains.ts` for untyped RPC access.
 */
export function useSetTeamMemberRole(teamId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation<void, Error, SetTeamMemberRoleArgs>({
    mutationKey: ['teamMembers', 'setRole', teamId],
    mutationFn: async ({ userId, role }) => {
      if (!teamId) throw new Error('No team')
      const untyped = supabase as unknown as {
        rpc: (
          fn: string,
          args?: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: { message: string } | null }>
      }
      const { error } = await untyped.rpc('set_team_member_role', {
        p_team_id: teamId,
        p_user_id: userId,
        p_role: role,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersKey(teamId ?? undefined) })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Accept invitation (RPC) — uses the `accept_team_invitation` SECURITY
// DEFINER function. Returns { team_id, team_name, already_member }.
// ──────────────────────────────────────────────────────────────────────────

export interface AcceptInvitationResult {
  team_id: string
  team_name: string
  already_member: boolean
}

/**
 * `supabase.functions.invoke` returns a `FunctionsHttpError` whose generic
 * message ("Edge Function returned a non-2xx status code") hides the actual
 * body. The body is reachable via `error.context` (a `Response`), so we
 * read it once and format something useful.
 */
async function extractFunctionError(err: unknown): Promise<string> {
  const fallback = (err as Error).message || 'Email failed to send'
  const ctx = (err as { context?: Response | undefined }).context
  if (!ctx || typeof ctx.text !== 'function') return fallback
  try {
    const text = await ctx.text()
    if (!text) return fallback
    try {
      const body = JSON.parse(text) as {
        error?: string
        message?: string
        requestId?: string
        retryAfterSeconds?: number
        status?: number
      }
      if (body.error === 'invite_email_rate_limited') {
        const wait = body.retryAfterSeconds ?? 300
        return `Invite email was sent recently. Try again in about ${Math.ceil(wait / 60)} minute(s).`
      }

      // Browser-facing edge functions intentionally return stable public
      // error codes, not provider/database internals. Include requestId when
      // present so support can correlate the client toast with server logs.
      const parts = [
        body.error || body.message,
        body.requestId ? `request ${body.requestId}` : null,
      ]
        .filter(Boolean)
        .map((s) => String(s))
      return parts.length ? parts.join(': ') : text
    } catch {
      return text
    }
  } catch {
    return fallback
  }
}

export function useAcceptInvitation() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['teamInvitations', 'accept'],
    mutationFn: async (token: string): Promise<AcceptInvitationResult> => {
      const { data, error } = await supabase.rpc('accept_team_invitation', { p_token: token })
      if (error) throw error
      // RPC returns a set — for the table-returning form, supabase-js
      // serialises as an array. Take the first row.
      const row = Array.isArray(data) ? data[0] : data
      if (!row) throw new Error('invitation_not_found')
      return row as AcceptInvitationResult
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamsKey(user?.id) })
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}
